import { defineComponent as _defineComponent } from 'vue'
import appCan from '@@/bits/app_can'
import { ContentPickerSource } from '@@/bits/content_picker'
import { dir } from '@@/bits/current_dir'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { recordWhitelistedClick } from '@@/bits/pepin'
import { defineAsyncComponent } from '@@/bits/vue'
import { useContentPickerStore } from '@@/pinia/content_picker'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContentPickerStore } from '@@/pinia/surface_content_picker'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { AttachmentProps, Post } from '@@/types'
import { usePostContentPickerSources } from '@@/vuecomposables/post_content_picker_sources'
import { usePostComposerModalsManager } from '@@/vuecomposables/usePostComposerModalsManager'
import { computed } from 'vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'SurfaceAddPost',
  setup(__props) {

const ContentPicker = defineAsyncComponent(() => import('@@/vuecomponents/ContentPicker.vue'))
const SurfaceContentPickerOld = defineAsyncComponent(() => import('@@/vuecomponents/content_picker_old.vue'))
const SurfaceAddPostButton = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceAddPostButton.vue'))
const SurfaceSectionPicker = defineAsyncComponent(() => import('@@/vuecomponents/surface_section_picker.vue'))

const { contentSources } = usePostContentPickerSources()

const postComposerModalsManager = usePostComposerModalsManager()
const contentPickerStore = useContentPickerStore()
const surfaceStore = useSurfaceStore()
const surfaceContentPickerStore = useSurfaceContentPickerStore()
const surfaceDraftsStore = useSurfaceDraftsStore()
const surfacePermissionsStore = useSurfacePermissionsStore()
const surfacePostsStore = useSurfacePostsStore()

const { showOverFileSizeModal, showExtensionRequired } = surfaceStore
const { uploadFileActiveDraft, updateActiveDraft } = surfaceDraftsStore
const { hidePostContentPicker } = surfaceContentPickerStore

const onUploadStart = ({ url }: { url?: string }) => {
  if (url) {
    surfacePostsStore.saveContent({ content: { link: url } })
  }
}

const onUploadDone = (url: string, attachmentProps?: AttachmentProps) => {
  contentPickerStore.hideContentPickerPanel()
  surfacePostsStore.saveContent({ content: { link: url, altText: attachmentProps?.altText } })
  if (surfaceDraftsStore.activeDraft?.shouldAutoPublish) {
    surfacePostsStore.savePost({ attributes: surfaceDraftsStore.activeDraft })
  }
}

const onUploadReject = (file: any) => {
  showOverFileSizeModal({ file })
}

const onUploadError = () => {
  surfaceDraftsStore.stopUploadFileActiveDraft()
  if (surfaceDraftsStore.activeDraft) {
    surfaceDraftsStore.stopAutoPublishDraft(surfaceDraftsStore.activeDraft.cid)
  }
}

const onUploadProgress = (progress: number) => {
  surfaceDraftsStore.updateActiveDraft({ uploadProgress: progress })
}

const setCaption = (caption: string) => {
  if (!caption) return

  surfacePostsStore.saveAttachmentCaption({ caption })
}

const canShowAddPost = computed(() => {
  const isAppWithNewPosting = device.app && appCan('newPostModal')
  const isActiveDraftDocked =
    postComposerModalsManager.hasDockedComposerModal.value &&
    surfaceDraftsStore.isActiveDraftNewPost &&
    device.desktop &&
    !device.phone
  const isAPostBeingEdited = !!surfacePostsStore.postBeingEditedCid
  return (
    !isAppWithNewPosting &&
    (surfaceStore.isEmbedded || !surfaceStore.isMobileLayout) &&
    surfacePermissionsStore.canIWrite &&
    !surfaceStore.isReadOnly &&
    (!isAPostBeingEdited || isActiveDraftDocked)
  )
})

const xAddPostButton = computed(() => {
  return canShowAddPost.value && !surfaceStore.isShelf && !surfaceStore.isSubmissionRequest
})

const xContentPicker = computed(() => {
  return (
    (contentPickerStore.xContentSourceMenu === true || contentPickerStore.activeContentPickerPanel !== null) &&
    surfaceContentPickerStore.xPostContentPicker
  )
})

const xSectionPicker = computed(() => {
  if (surfaceStore.canUseSections && surfaceStore.isSectioned) {
    const pendingPostsWithoutSections = surfacePostsStore.pendingPosts.filter((p: Post) => !p.wall_section_id)
    return pendingPostsWithoutSections.length > 0
  }
  return false
})

const xSearchBar = computed(() => {
  return contentSources.value
    .flat()
    .filter((source) => source.visible)
    .some((source) =>
      [
        ContentPickerSource.ImageSearch,
        ContentPickerSource.GifSearch,
        ContentPickerSource.VideoSearch,
        ContentPickerSource.AudioSearch,
        ContentPickerSource.WebBrowser,
        ContentPickerSource.LinkInput,
        ContentPickerSource.AiImageSearch,
      ].includes(source.type as ContentPickerSource),
    )
})

const googleDriveUploadKey = computed(() => {
  return surfaceDraftsStore.activeDraft ? `draft:${surfaceDraftsStore.activeDraft.cid}` : undefined
})

return { __sfc: true,ContentPicker, SurfaceContentPickerOld, SurfaceAddPostButton, SurfaceSectionPicker, contentSources, postComposerModalsManager, contentPickerStore, surfaceStore, surfaceContentPickerStore, surfaceDraftsStore, surfacePermissionsStore, surfacePostsStore, showOverFileSizeModal, showExtensionRequired, uploadFileActiveDraft, updateActiveDraft, hidePostContentPicker, onUploadStart, onUploadDone, onUploadReject, onUploadError, onUploadProgress, setCaption, canShowAddPost, xAddPostButton, xContentPicker, xSectionPicker, xSearchBar, googleDriveUploadKey, dir, device, isAppUsing, recordWhitelistedClick }
}

})